/**
 * Workspace Ticket Entity Descriptors
 *
 * Descriptors for the ticket entity and its subcollection entities.
 *
 * Storage layout:
 *   org/{orgId}/ticket/{ticketId}
 *   org/{orgId}/ticket/{ticketId}/message/{messageId}
 *   org/{orgId}/ticket/{ticketId}/attachment/{attachmentId}
 *   org/{orgId}/ticket/{ticketId}/participant/{participantId}
 *   org/{orgId}/ticket/{ticketId}/relation/{relationId}
 *   org/{orgId}/ticket/{ticketId}/activity/{activityId}
 *   ticketLink/{linkId}  (root-level, cross-org, managed by system-api)
 */
import { CacheStrategy, DataSourceStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { SYSTEM_ALLOWED_DATA_SOURCES } from './dataSourcePolicy.js';
import { WORKSPACE_TICKET, TICKET_MESSAGE, TICKET_ATTACHMENT, TICKET_PARTICIPANT, TICKET_RELATION, TICKET_LINK, TICKET_ACTIVITY_LOG, } from '../types/workspace-ticket.js';
import { FIELD_LIMITS, MAX_EFFORT_MS, MAX_QUANTITY } from '../validation/fieldLimits.js';
export const workspaceTicketDescriptor = {
    name: WORKSPACE_TICKET,
    label: 'Ticket',
    domain: 'tickets',
    collectionPath: 'org/{orgId}/ticket',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 1,
    description: 'Main ticket entity in the workspace-based ticket system',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        { name: 'organizationId', label: 'Organização', type: 'string', required: true },
        { name: 'workspaceId', label: 'Espaço', type: 'string', required: true },
        {
            name: 'number',
            label: 'Número',
            type: 'number',
            required: false,
            description: 'Sequential number within the workspace (assigned by system-api on create)',
        },
        {
            name: 'numberLabel',
            label: 'Rótulo do número',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
            description: 'Human-readable ticket label (e.g. TI-123), assigned by system-api on create',
        },
        {
            name: 'currentStageId',
            label: 'Estágio atual',
            type: 'string',
            required: true,
        },
        {
            name: 'lifecycleStatus',
            label: 'Status do ciclo de vida',
            type: 'enum',
            required: true,
            enumValues: ['OPEN', 'ON_HOLD', 'CANCELLED', 'CLOSED'],
            description: 'High-level lifecycle state, independent of workflow stage',
        },
        {
            name: 'statusMessage',
            label: 'Mensagem de status',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.STATUS_MESSAGE,
        },
        {
            name: 'title',
            label: 'Título',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.TITLE,
        },
        {
            name: 'details',
            label: 'Detalhes',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.TICKET_DETAILS,
            description: 'Main request description',
        },
        {
            name: 'definitionMarkdown',
            label: 'Definição',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.TICKET_MARKDOWN,
            description: 'Working notes, scope, plan, or implementation definition (markdown)',
        },
        {
            name: 'type',
            label: 'Tipo',
            type: 'enum',
            required: false,
            enumValues: ['BUG', 'IMPROVEMENT', 'REQUEST', 'QUESTION'],
        },
        {
            name: 'priority',
            label: 'Prioridade',
            type: 'enum',
            required: true,
            enumValues: ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'],
        },
        // originContext: TicketOriginContext — omitted; complex object validated at service layer
        { name: 'requesterUserId', label: 'ID do solicitante', type: 'string', required: true },
        {
            name: 'requesterUserName',
            label: 'Solicitante',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.USER_NAME,
        },
        { name: 'assigneeUserId', label: 'ID do responsável', type: 'string', required: false },
        {
            name: 'assigneeUserName',
            label: 'Responsável',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.USER_NAME,
        },
        { name: 'createdAt', label: 'Criado em', type: 'date', display: 'datetime', required: false },
        { name: 'requestedAt', label: 'Solicitado para', type: 'date', required: false },
        { name: 'dueAt', label: 'Vence em', type: 'date', display: 'datetime', required: false },
        { name: 'expectedCompletionAt', label: 'Conclusão prevista', type: 'date', display: 'datetime', required: false },
        { name: 'completedAt', label: 'Concluído em', type: 'date', display: 'datetime', required: false },
        { name: 'cancelledAt', label: 'Cancelado em', type: 'date', display: 'datetime', required: false },
        { name: 'updatedAt', label: 'Atualizado em', type: 'date', display: 'datetime', required: false },
        {
            name: 'estimatedEffortMs',
            label: 'Esforço estimado (ms)',
            type: 'number',
            display: 'duration',
            required: false,
            description: `Quoted effort in ms, 0–${MAX_EFFORT_MS} (UI displays as hours)`,
        },
        {
            name: 'approvedEffortMs',
            label: 'Esforço aprovado (ms)',
            type: 'number',
            display: 'duration',
            required: false,
            description: `Approved effort in ms, 0–${MAX_EFFORT_MS} (UI displays as hours)`,
        },
        {
            name: 'estimatedCost',
            label: 'Custo estimado',
            type: 'number',
            display: 'money',
            required: false,
            description: `Non-negative, max ${MAX_QUANTITY}`,
        },
        {
            name: 'approvedCost',
            label: 'Custo aprovado',
            type: 'number',
            display: 'money',
            required: false,
            description: `Non-negative, max ${MAX_QUANTITY}`,
        },
        { name: 'requestedStartDate', label: 'Início solicitado', type: 'date', required: false },
        { name: 'promisedDeliveryDate', label: 'Entrega prometida', type: 'date', required: false },
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'workspaceId',
            targetEntity: 'TicketWorkspace',
            relationship: 'many-to-one',
            displayField: 'name',
            description: 'Workspace this ticket belongs to',
        },
        {
            fieldName: 'id',
            targetEntity: 'TicketMessage',
            relationship: 'one-to-many',
            description: 'Message thread',
        },
        {
            fieldName: 'id',
            targetEntity: 'TicketAttachment',
            relationship: 'one-to-many',
            description: 'File attachments',
        },
        {
            fieldName: 'id',
            targetEntity: 'TicketParticipant',
            relationship: 'one-to-many',
            description: 'Explicit participant list',
        },
        {
            fieldName: 'id',
            targetEntity: 'TicketRelation',
            relationship: 'one-to-many',
            description: 'Relations to other tickets',
        },
        {
            fieldName: 'id',
            targetEntity: 'TicketActivityLog',
            relationship: 'one-to-many',
            description: 'Immutable audit log',
        },
    ],
};
export const ticketMessageDescriptor = {
    name: TICKET_MESSAGE,
    label: 'Mensagem do ticket',
    domain: 'tickets',
    collectionPath: 'org/{orgId}/ticket/{ticketId}/message',
    pathPatternKeys: {
        orgId: 'string',
        ticketId: 'string',
    },
    version: 1,
    description: 'Message or system event in the ticket thread',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        { name: 'ticketId', label: 'Ticket', type: 'string', required: true },
        { name: 'authorUserId', label: 'ID do autor', type: 'string', required: true },
        {
            name: 'authorUserName',
            label: 'Autor',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.USER_NAME,
        },
        {
            name: 'body',
            label: 'Corpo',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.MESSAGE_BODY,
        },
        {
            name: 'type',
            label: 'Tipo',
            type: 'enum',
            required: true,
            enumValues: ['COMMENT', 'INTERNAL_NOTE', 'SYSTEM_EVENT', 'APPROVAL_REQUEST', 'APPROVAL_RESPONSE'],
        },
        {
            name: 'visibility',
            label: 'Visibilidade',
            type: 'enum',
            required: true,
            enumValues: ['PUBLIC', 'INTERNAL', 'REQUESTER_ONLY'],
        },
        { name: 'createdAt', label: 'Criado em', type: 'date', display: 'datetime', required: false },
    ],
    foreignKeys: [
        {
            fieldName: 'ticketId',
            targetEntity: 'WorkspaceTicket',
            relationship: 'many-to-one',
            description: 'Parent ticket',
        },
    ],
};
export const ticketAttachmentDescriptor = {
    name: TICKET_ATTACHMENT,
    label: 'Anexo do ticket',
    domain: 'tickets',
    collectionPath: 'org/{orgId}/ticket/{ticketId}/attachment',
    pathPatternKeys: {
        orgId: 'string',
        ticketId: 'string',
    },
    version: 1,
    description: 'File attachment metadata for a ticket',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        { name: 'ticketId', label: 'Ticket', type: 'string', required: true },
        {
            name: 'fileName',
            label: 'Nome do arquivo',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.FILE_NAME,
        },
        {
            name: 'mimeType',
            label: 'Tipo MIME',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.MIME_TYPE,
        },
        {
            name: 'storagePath',
            label: 'Caminho no storage',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.STORAGE_PATH,
            description: 'Path in cloud storage',
        },
        {
            name: 'url',
            label: 'URL de download',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.STORAGE_PATH,
        },
        {
            name: 'sizeBytes',
            label: 'Tamanho (bytes)',
            type: 'number',
            required: false,
            description: 'File size in bytes. Non-negative.',
        },
        { name: 'uploadedByUserId', label: 'Enviado por', type: 'string', required: true },
        { name: 'uploadedAt', label: 'Enviado em', type: 'date', display: 'datetime', required: false },
        {
            name: 'visibility',
            label: 'Visibilidade',
            type: 'enum',
            required: false,
            enumValues: ['PUBLIC', 'INTERNAL', 'REQUESTER_ONLY'],
        },
    ],
};
export const ticketParticipantDescriptor = {
    name: TICKET_PARTICIPANT,
    label: 'Participante do ticket',
    domain: 'tickets',
    collectionPath: 'org/{orgId}/ticket/{ticketId}/participant',
    pathPatternKeys: {
        orgId: 'string',
        ticketId: 'string',
    },
    version: 1,
    description: 'Explicit user involvement record in a ticket',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        { name: 'ticketId', label: 'Ticket', type: 'string', required: true },
        { name: 'userId', label: 'ID do usuário', type: 'string', required: true },
        {
            name: 'userName',
            label: 'Nome do usuário',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.USER_NAME,
        },
        {
            name: 'role',
            label: 'Papel',
            type: 'enum',
            required: true,
            enumValues: ['REQUESTER', 'ASSIGNEE', 'WATCHER', 'APPROVER', 'OPERATOR'],
        },
        { name: 'addedAt', label: 'Adicionado em', type: 'date', display: 'datetime', required: false },
    ],
};
export const ticketRelationDescriptor = {
    name: TICKET_RELATION,
    label: 'Relação do ticket',
    domain: 'tickets',
    collectionPath: 'org/{orgId}/ticket/{ticketId}/relation',
    pathPatternKeys: {
        orgId: 'string',
        ticketId: 'string',
    },
    version: 1,
    description: 'Typed relation link between two tickets',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        { name: 'ticketId', label: 'Ticket', type: 'string', required: true },
        { name: 'relatedTicketId', label: 'Ticket relacionado', type: 'string', required: true },
        {
            name: 'relatedOrganizationId',
            label: 'Organização relacionada',
            type: 'string',
            required: true,
            description: 'Organization where relatedTicketId lives (may differ for cross-org)',
        },
        {
            name: 'relationType',
            label: 'Tipo de relação',
            type: 'enum',
            required: true,
            enumValues: ['PARENT_CHILD', 'DEPENDS_ON', 'BLOCKS', 'RELATED_TO', 'MIRRORS'],
        },
        { name: 'createdByUserId', label: 'Criado por', type: 'string', required: true },
        { name: 'createdAt', label: 'Criado em', type: 'date', display: 'datetime', required: false },
    ],
};
export const ticketLinkDescriptor = {
    name: TICKET_LINK,
    label: 'Vínculo de ticket',
    domain: 'tickets',
    collectionPath: 'ticketLink',
    pathPatternKeys: {},
    version: 1,
    description: 'Cross-organization synchronization link between mirrored tickets. ' +
        'Root-level collection; all operations mediated by system-api.',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        {
            name: 'sourceOrganizationId',
            label: 'Organização de origem',
            type: 'string',
            required: true,
        },
        { name: 'sourceTicketId', label: 'Ticket de origem', type: 'string', required: true },
        {
            name: 'targetOrganizationId',
            label: 'Organização de destino',
            type: 'string',
            required: true,
        },
        { name: 'targetTicketId', label: 'Ticket de destino', type: 'string', required: true },
        {
            name: 'linkType',
            label: 'Tipo de vínculo',
            type: 'enum',
            required: true,
            enumValues: ['MIRROR', 'UPSTREAM_DOWNSTREAM'],
        },
        {
            name: 'syncPolicy',
            label: 'Política de sincronização',
            type: 'enum',
            required: true,
            enumValues: ['MANUAL', 'AUTO_STAGE', 'AUTO_FULL'],
        },
        { name: 'createdByUserId', label: 'Criado por', type: 'string', required: true },
        { name: 'createdAt', label: 'Criado em', type: 'date', display: 'datetime', required: false },
    ],
};
export const ticketActivityLogDescriptor = {
    name: TICKET_ACTIVITY_LOG,
    label: 'Histórico do ticket',
    domain: 'tickets',
    collectionPath: 'org/{orgId}/ticket/{ticketId}/activity',
    pathPatternKeys: {
        orgId: 'string',
        ticketId: 'string',
    },
    version: 1,
    description: 'Immutable audit event for a ticket (stage change, assignment, messages, etc.)',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        { name: 'ticketId', label: 'Ticket', type: 'string', required: true },
        {
            name: 'eventType',
            label: 'Tipo de evento',
            type: 'enum',
            required: true,
            enumValues: [
                'CREATED',
                'STAGE_CHANGED',
                'LIFECYCLE_STATUS_CHANGED',
                'ASSIGNEE_CHANGED',
                'FIELDS_UPDATED',
                'MESSAGE_ADDED',
                'ATTACHMENT_ADDED',
                'ATTACHMENT_REMOVED',
                'PARTICIPANT_ADDED',
                'PARTICIPANT_REMOVED',
                'RELATION_ADDED',
                'AUTOMATION_EXECUTED',
            ],
        },
        { name: 'actorUserId', label: 'Ator', type: 'string', required: true },
        {
            name: 'actorUserName',
            label: 'Nome do ator',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.USER_NAME,
        },
        // data: Record<string, unknown> — omitted; structured diff stored per event type
        { name: 'createdAt', label: 'Criado em', type: 'date', display: 'datetime', required: true },
    ],
    foreignKeys: [
        {
            fieldName: 'ticketId',
            targetEntity: 'WorkspaceTicket',
            relationship: 'many-to-one',
            description: 'Parent ticket',
        },
    ],
};
//# sourceMappingURL=workspace-ticket.descriptor.js.map